import { type ComponentProps, type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import { cn } from '@/lib/utils';
import { assignItemsIncrementally, buildColumnGroups, UNASSIGNED_COLUMN } from './layout';

export interface MasonryGridLayoutProps extends ComponentProps<'div'> {
  items: ReactNode[];
  itemsLayoutKey: string;
  className?: string;
}

function MeasuredItem({
  index,
  registerHeight,
  children,
}: {
  index: number;
  registerHeight: (index: number, height: number) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    const element = ref.current;
    if (element == null) return;

    const reportHeight = () => {
      registerHeight(index, element.offsetHeight);
    };

    reportHeight();

    const resizeObserver = new ResizeObserver(reportHeight);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [index, registerHeight]);

  return <div ref={ref}>{children}</div>;
}

export function MeasuredMasonryGrid({
  items,
  itemsLayoutKey,
  gapValue,
  columns,
  className,
  ...props
}: MasonryGridLayoutProps & { gapValue: number; columns: 1 | 2 | 3 }) {
  const [heights, setHeights] = useState(() => items.map(() => 0));
  const [columnByIndex, setColumnByIndex] = useState(() => items.map(() => UNASSIGNED_COLUMN));

  useIsomorphicLayoutEffect(() => {
    setHeights(Array.from({ length: items.length }, () => 0));
    setColumnByIndex(Array.from({ length: items.length }, () => UNASSIGNED_COLUMN));
  }, [itemsLayoutKey, items.length]);

  const registerHeight = useCallback((index: number, height: number) => {
    setHeights((currentHeights) => {
      if (index < 0 || index >= currentHeights.length) return currentHeights;
      if (currentHeights[index] === height) return currentHeights;

      const nextHeights = [...currentHeights];
      nextHeights[index] = height;
      return nextHeights;
    });
  }, []);

  useIsomorphicLayoutEffect(() => {
    setColumnByIndex((currentColumns) =>
      assignItemsIncrementally(heights, currentColumns, columns, gapValue),
    );
  }, [heights, columns, gapValue]);

  const columnAssignments = useMemo(
    () => buildColumnGroups(columnByIndex, columns),
    [columnByIndex, columns],
  );

  return (
    <div className={cn('flex w-full items-start', className)} {...props}>
      {columnAssignments.map((indices, columnIndex) => (
        <div
          key={
            columnIndex === 0
              ? 'masonry-col-primary'
              : columnIndex === 1
                ? 'masonry-col-secondary'
                : 'masonry-col-tertiary'
          }
          className="flex min-w-0 flex-1 flex-col"
          style={{ gap: gapValue }}
        >
          {indices.map((itemIndex) => (
            <MeasuredItem key={itemIndex} index={itemIndex} registerHeight={registerHeight}>
              {items[itemIndex]}
            </MeasuredItem>
          ))}
        </div>
      ))}
    </div>
  );
}
